/**
 * Client legal documents (OPS Documents Phase 1).
 *
 * Staff upload a client's legal paperwork (UAE trade licence, VAT certificate,
 * corporate tax certificate, or OTHER). Non-OTHER extraction (tax PDF text
 * first, otherwise vision) is ALWAYS a draft — nothing reaches the
 * client record until a reviewer confirms it (documents.confirm capability).
 */

import { z } from 'zod';
import { idSchema, iso8601Schema } from './common';
import { JURISDICTION_CODES } from '../reference/jurisdictions';
import { taxDateSchema, taxReviewFieldsSchema } from './tax';

export const clientDocumentTypeSchema = z.enum([
  'TRADE_LICENSE',
  'VAT_CERTIFICATE',
  'CORPORATE_TAX_CERTIFICATE',
  'CERTIFICATE_OF_INCORPORATION',
  'PASSPORT',
  'EMIRATES_ID',
  'ESTABLISHMENT_CARD',
  'OTHER',
]);
export type ClientDocumentType = z.infer<typeof clientDocumentTypeSchema>;

export const CLIENT_DOCUMENT_TYPE_LABELS: Record<ClientDocumentType, string> = {
  TRADE_LICENSE: 'Trade license',
  VAT_CERTIFICATE: 'VAT certificate',
  CORPORATE_TAX_CERTIFICATE: 'Corporate tax certificate',
  CERTIFICATE_OF_INCORPORATION: 'Certificate of incorporation',
  PASSPORT: 'Passport',
  EMIRATES_ID: 'Emirates ID',
  ESTABLISHMENT_CARD: 'Establishment card',
  OTHER: 'Other',
};

export const clientDocumentStatusSchema = z.enum([
  'PENDING_REVIEW',
  'EXTRACTION_FAILED',
  'CONFIRMED',
  'SUPERSEDED',
]);
export type ClientDocumentStatus = z.infer<typeof clientDocumentStatusSchema>;

/** OPS 3.0a unified store: per-client areas. Vault rows are LEGAL. */
export const clientDocumentAreaSchema = z.enum([
  'LEGAL',
  'ENGAGEMENTS',
  'COMPLIANCE',
  'PROPOSALS',
  'INVOICES',
]);
export type ClientDocumentArea = z.infer<typeof clientDocumentAreaSchema>;

export const CLIENT_AREA_LABELS: Record<ClientDocumentArea, string> = {
  LEGAL: 'Legal',
  ENGAGEMENTS: 'Engagements',
  COMPLIANCE: 'Compliance',
  PROPOSALS: 'Proposals',
  INVOICES: 'Invoices',
};

export const CLIENT_DOCUMENT_STATUS_LABELS: Record<ClientDocumentStatus, string> = {
  PENDING_REVIEW: 'Pending review',
  EXTRACTION_FAILED: 'Extraction failed',
  CONFIRMED: 'Confirmed',
  SUPERSEDED: 'Superseded',
};

/** YYYY-MM-DD (date only, no time). */
export const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

// ── LLM extraction drafts ─────────────────────────────────────────────────────
// Every field is independently nullable and `catch(null)` — a partially
// readable document still yields a usable draft instead of failing outright.

const draftText = z.string().max(300).nullable().catch(null);
const draftDate = dateOnlySchema.nullable().catch(null);

export const tradeLicenseExtractionSchema = z.object({
  legalName: draftText,
  /** The PERSON named on the license as manager/director/partner — maps to the client contact name, NOT the legal entity. */
  managerName: draftText,
  licenseNumber: draftText,
  establishmentDate: draftDate,
  issueDate: draftDate,
  expiryDate: draftDate,
  activities: draftText,
  issuingAuthority: draftText,
});
export type TradeLicenseExtraction = z.infer<typeof tradeLicenseExtractionSchema>;

const taxDraftFields = {
  effectiveDate: taxDateSchema.nullable().catch(null),
  issueDate: taxDateSchema.nullable().catch(null),
  firstPeriodStartDate: taxDateSchema.nullable().catch(null),
  firstPeriodEndDate: taxDateSchema.nullable().catch(null),
  firstReturnDueDate: taxDateSchema.nullable().catch(null),
  taxPeriodPattern: z.string().max(200).nullable().catch(null),
};
export const vatCertificateExtractionSchema = z.object({
  legalName: draftText,
  trn: draftText,
  ...taxDraftFields,
});
export type VatCertificateExtraction = z.infer<typeof vatCertificateExtractionSchema>;

export const corporateTaxExtractionSchema = z.object({
  legalName: draftText,
  trn: draftText,
  registrationNumber: draftText,
  ...taxDraftFields,
});
export type CorporateTaxExtraction = z.infer<typeof corporateTaxExtractionSchema>;

export const certificateOfIncorporationExtractionSchema = z.object({
  legalName: draftText,
  registrationNumber: draftText,
  incorporationDate: draftDate,
  issuingAuthority: draftText,
});
export type CertificateOfIncorporationExtraction = z.infer<typeof certificateOfIncorporationExtractionSchema>;

export const passportExtractionSchema = z.object({
  holderName: draftText,
  passportNumber: draftText,
  nationality: draftText,
  issueDate: draftDate,
  expiryDate: draftDate,
  issuingAuthority: draftText,
});
export type PassportExtraction = z.infer<typeof passportExtractionSchema>;

export const emiratesIdExtractionSchema = z.object({
  holderName: draftText,
  idNumber: draftText,
  issueDate: draftDate,
  expiryDate: draftDate,
});
export type EmiratesIdExtraction = z.infer<typeof emiratesIdExtractionSchema>;

export const establishmentCardExtractionSchema = z.object({
  holderName: draftText,
  cardNumber: draftText,
  issueDate: draftDate,
  expiryDate: draftDate,
  issuingAuthority: draftText,
});
export type EstablishmentCardExtraction = z.infer<typeof establishmentCardExtractionSchema>;

/** Loose shape stored on the document row (draft, per-type keys). */
export const extractedFieldsSchema = z.record(z.string(), z.string().nullable());
export type ExtractedFields = z.infer<typeof extractedFieldsSchema>;

// ── API payloads ──────────────────────────────────────────────────────────────

export const clientDocumentOutputSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  type: clientDocumentTypeSchema,
  status: clientDocumentStatusSchema,
  area: clientDocumentAreaSchema,
  originalName: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
  issueDate: iso8601Schema.nullable(),
  expiryDate: iso8601Schema.nullable(),
  extracted: extractedFieldsSchema.nullable(),
  extractionError: z.string().nullable(),
  invoiceId: idSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  proposalId: idSchema.nullable(),
  proposalSubject: z.string().nullable(),
  complianceItemId: idSchema.nullable(),
  /** Documents Phase 3: auto-created compliance renewal item driven by expiryDate. */
  renewalItemId: idSchema.nullable(),
  /** OPS Historic archive: manual-only archived copies (Historic folder per client, same five areas). */
  historic: z.boolean(),
  uploadedById: idSchema.nullable(),
  uploadedByName: z.string().nullable(),
  confirmedById: idSchema.nullable(),
  confirmedByName: z.string().nullable(),
  confirmedAt: iso8601Schema.nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type ClientDocumentOutput = z.infer<typeof clientDocumentOutputSchema>;

/** Documents page client tree: scoped clients with per-area counts. */
export const documentTreeEntrySchema = z.object({
  id: idSchema,
  clientCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  counts: z.record(clientDocumentAreaSchema, z.number().int().nonnegative()),
  /** Historic archive counts per area (same five subfolders as live — shared enum). */
  historicCounts: z.record(clientDocumentAreaSchema, z.number().int().nonnegative()),
});
export type DocumentTreeEntry = z.infer<typeof documentTreeEntrySchema>;

/** OPS 3.0b: one compliance item folder inside a jurisdiction folder. */
export const complianceFolderItemSchema = z.object({
  id: idSchema,
  serviceType: z.string(),
  jurisdiction: z.string().nullable(),
  status: z.string(),
  periodType: z.string().nullable(),
  periodValue: z.string().nullable(),
  deadline: iso8601Schema.nullable(),
  evidenceCount: z.number().int().nonnegative(),
});
export type ComplianceFolderItem = z.infer<typeof complianceFolderItemSchema>;

/** OPS 3.0b: jurisdiction folder in a client's Compliance area. */
export const complianceFolderSchema = z.object({
  jurisdiction: z.string().nullable(),
  items: z.array(complianceFolderItemSchema),
});
export type ComplianceFolder = z.infer<typeof complianceFolderSchema>;

/** Client fields the review gate can write. */
export const confirmableFieldSchema = z.enum([
  'name',
  'legalName',
  'tradeLicenseNo',
  'trn',
  'incorporationDate',
  'jurisdiction',
  'fyEnd',
]);
export type ConfirmableField = z.infer<typeof confirmableFieldSchema>;

export const confirmClientDocumentInputSchema = z.object({
  /** Reviewer-edited mapped values. Null/absent = do not write that field. */
  fields: z.object({
    name: z.string().max(200).nullable().optional(),
    legalName: z.string().max(300).nullable().optional(),
    tradeLicenseNo: z.string().max(100).nullable().optional(),
    trn: z.string().max(50).nullable().optional(),
    incorporationDate: dateOnlySchema.nullable().optional(),
    jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable().optional(),
    fyEnd: dateOnlySchema.nullable().optional(),
  }).default({}),
  /** Fields the reviewer explicitly allows to overwrite a non-empty client value. */
  applyConflicts: z.array(confirmableFieldSchema).default([]),
  /** Documents Phase 3: reviewer-edited DOCUMENT dates (extraction draft may
   * miss them, or the doc prints none). Absent key = keep the stored value. */
  docDates: z.object({
    issueDate: dateOnlySchema.nullable().optional(),
    expiryDate: dateOnlySchema.nullable().optional(),
  }).optional(),
  taxFields: taxReviewFieldsSchema.optional(),
});
export type ConfirmClientDocumentInput = z.infer<typeof confirmClientDocumentInputSchema>;

/** Documents Phase 3: manual date edit on a CONFIRMED document (detail view). */
export const patchClientDocumentDatesInputSchema = z.object({
  issueDate: dateOnlySchema.nullable().optional(),
  expiryDate: dateOnlySchema.nullable().optional(),
}).refine((v) => v.issueDate !== undefined || v.expiryDate !== undefined, {
  message: 'Provide issueDate and/or expiryDate',
});
export type PatchClientDocumentDatesInput = z.infer<typeof patchClientDocumentDatesInputSchema>;

export const confirmClientDocumentOutputSchema = z.object({
  document: clientDocumentOutputSchema,
  /** Client fields actually written. */
  written: z.array(confirmableFieldSchema),
  /** Non-empty client fields left untouched because they were not in applyConflicts. */
  skippedConflicts: z.array(confirmableFieldSchema),
});
export type ConfirmClientDocumentOutput = z.infer<typeof confirmClientDocumentOutputSchema>;
